import { GridApi, IFilterParams } from 'ag-grid-community';
import { OrganizationMultiSelect } from 'components/selects/OrganizationSelect';
import { FilterChangeCallback } from 'components/tables/filters/hooks/use-filter-value';
import { get, isEqual } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

export const FILTER_KEY = 'organization.organizationId' as const;

interface OrganizationMultiSelectFilterModel {
  filter: string[];
}

interface ConfigureOrganizationMultiSelectFilterProps {
  permissionSlug?: string;
}

interface OrganizationMultiSelectFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
  permissionSlug?: string;
}

interface ConfiguredOrganizationMultiSelectFilterProps {
  api: GridApi;
  onChange?: FilterChangeCallback;
}

interface OrganizationMultiSelectFilterRef {
  onParentModelChanged: () => void;
}

/**
 * Normalizes unknown filter values to a stable string array.
 */
const normalizeValue = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean);
  }

  if (value === null || value === undefined || value === -1 || value === '-1' || value === '') {
    return [];
  }

  return [String(value)];
};

/**
 * AG Grid parent filter for matching rows whose organization belongs to the selected set.
 */
export const OrganizationMultiColumnFilter = forwardRef(({ filterChangedCallback, colDef }: IFilterParams, ref) => {
  const value = useRef<string[]>([]);

  useImperativeHandle(ref, () => ({
    doesFilterPass({ data }: { data: Record<string, any> }) {
      if (value.current.length === 0) {
        return true;
      }

      const field = colDef.field ?? FILTER_KEY;
      const organizationId = get(data, field);

      return value.current.includes(String(organizationId));
    },

    getModel() {
      if (value.current.length === 0) {
        return null;
      }

      return { filter: value.current };
    },

    setModel(model: OrganizationMultiSelectFilterModel | null) {
      value.current = normalizeValue(model?.filter);
    },

    isFilterActive() {
      return value.current.length > 0;
    },

    onFloatingFilterChanged(newValue: string[]) {
      if (!isEqual(value.current, newValue)) {
        value.current = normalizeValue(newValue);
        filterChangedCallback();
      }
    },
  }));

  return <></>;
});

/**
 * Floating filter for the organization column. Displays a multi-select with all accessible organizations.
 */
const OrganizationMultiSelectFilter = forwardRef<OrganizationMultiSelectFilterRef, OrganizationMultiSelectFilterProps>(
  function OrganizationMultiSelectFilter({ api, onChange, permissionSlug }, ref) {
    const [value, setValue] = useState<string[]>(() => normalizeValue(api.getFilterModel()[FILTER_KEY]?.filter));

    const syncValueFromGrid = useCallback(
      () => setValue(normalizeValue(api.getFilterModel()[FILTER_KEY]?.filter)),
      [api]
    );

    useImperativeHandle(ref, () => ({ onParentModelChanged: syncValueFromGrid }), [syncValueFromGrid]);

    const pushModel = useCallback(
      (nextValue: string[]) => {
        const normalizedValue = normalizeValue(nextValue);
        const nextModel = normalizedValue.length > 0 ? { filter: normalizedValue } : null;

        if (onChange) {
          onChange({ [FILTER_KEY]: nextModel });
          return;
        }

        api.setFilterModel({
          ...api.getFilterModel(),
          [FILTER_KEY]: nextModel,
        });
      },
      [api, onChange]
    );

    const setAndPush = useCallback(
      (nextValue: string[]) => {
        const normalizedValue = normalizeValue(nextValue);

        setValue(normalizedValue);
        pushModel(normalizedValue);
      },
      [pushModel]
    );

    useEffect(() => {
      /**
       * Keeps the floating filter in sync with external grid model updates.
       */
      const handleFilterChanged = () => syncValueFromGrid();

      api.addEventListener('filterChanged', handleFilterChanged);
      return () => api.removeEventListener('filterChanged', handleFilterChanged);
    }, [api, syncValueFromGrid]);

    return (
      <OrganizationMultiSelect
        lazyLoad
        clearable
        compact
        w="100%"
        size="sm"
        placeholder="Všetky organizácie"
        value={value}
        onChange={setAndPush}
        permissionSlug={permissionSlug}
        showDiscarded
      />
    );
  }
);

type OrganizationMultiSelectFilterComponent = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<OrganizationMultiSelectFilterProps> & React.RefAttributes<OrganizationMultiSelectFilterRef>
> & {
  configure: (
    config: ConfigureOrganizationMultiSelectFilterProps
  ) => (props: ConfiguredOrganizationMultiSelectFilterProps) => React.JSX.Element;
};

const OrganizationMultiSelectFilterWithConfigure =
  OrganizationMultiSelectFilter as OrganizationMultiSelectFilterComponent;

OrganizationMultiSelectFilterWithConfigure.configure =
  (config: ConfigureOrganizationMultiSelectFilterProps) => (props: ConfiguredOrganizationMultiSelectFilterProps) =>
    <OrganizationMultiSelectFilterWithConfigure {...config} {...props} />;

export default OrganizationMultiSelectFilterWithConfigure;
